// Die eine Dokumentvorschau. Vorher lag dasselbe Modal viermal kopiert im Code
// (DocumentItem, DocumentItemTable, CompanyCertificateCard, CertificateUploadList) —
// hier steht es einmal, samt Laden der URL, typisiertem Blob und Aufraeumen.
//
// Aufruf: `document` auf den anzuzeigenden Datensatz setzen, `null` schliesst.
//
//   const [previewDoc, setPreviewDoc] = useState(null);
//   <i onClick={() => setPreviewDoc(doc)} />
//   <DocumentPreviewModal document={previewDoc} onHide={() => setPreviewDoc(null)} />
//
// Die URL holt die Komponente selbst — der Aufrufer braucht keinen Spinner und keinen
// eigenen Ladezustand. Wer die Datei nicht aus `Documents` bekommt (CRM-Mailanhaenge
// kommen live aus dem Postfach), gibt `getUrl` mit. `options` geht unveraendert an
// getDocumentURL — dort landen `recordType`, `compressed` und `thumbnailSize`.
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Spinner from "./Spinner";
import HiddenValue from "./HiddenValue";
import { getDocumentURL } from "../helpers/api/document";
import { titleIfVisible } from "../helpers/hideData";
import { useHideData } from "../hooks/useHideData";

/** Was das Modal darstellen kann — Rest bekommt nur den Hinweis zum Herunterladen. */
export const canPreviewDocument = (doc) => {
  const type = doc?.fileType || "";
  return (
    type === "application/pdf" ||
    type.startsWith("image/") ||
    // Der fileType fehlt an manchen Altdatensaetzen; dann entscheidet die Endung.
    /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/i.test(doc?.name || "")
  );
};

const isPdf = (doc) =>
  doc?.fileType === "application/pdf" ||
  (!doc?.fileType && /\.pdf$/i.test(doc?.name || ""));

const DocumentPreviewModal = ({ document: doc, onHide, getUrl, options }) => {
  const { t } = useTranslation(["components", "common"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));
  // Privacy Mode: the file name appears three times here — the title, the iframe `title` and
  // the img `alt`. titleIfVisible() reads the module config and does not re-render on its own,
  // so without this subscription those two attributes would keep the real name on a toggle
  // while the title is already masked.
  useHideData();
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Every caller closes this by setting its `document` prop to null, which used
  // to blank the body one frame into the fade-out: with no document,
  // canPreviewDocument() is false, so the preview was replaced by the "no
  // preview for this file type, please download" fallback for the length of the
  // animation. It looked like an error on the way out.
  //
  // So the last document is held locally and only dropped once the modal has
  // actually finished hiding (onExited). `show` still follows the prop, so the
  // close itself is unchanged — only the content outlives it.
  const [shown, setShown] = useState(doc);
  useEffect(() => {
    if (doc) setShown(doc);
  }, [doc]);
  const view = doc || shown;

  const triggerDownload = (downloadUrl, filename) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async () => {
    // `view`, not `doc`: during the fade-out the prop is already null, and the
    // button is still on screen until the animation ends. Guarding on `doc`
    // made a click in that window silently do nothing.
    if (!view || downloading) return;
    if (url) {
      triggerDownload(url, view.name);
      return;
    }
    setDownloading(true);
    try {
      const raw = getUrl
        ? await getUrl(view)
        : await getDocumentURL(view.id, {
            userCountry: user?.country,
            ...options,
          });
      triggerDownload(raw, view.name);
    } catch (err) {
      console.error("Vorschau-Download fehlgeschlagen", err);
      toast.error(
        t(
          "The document couldn't be downloaded. Please refresh the page and try again."
        )
      );
    } finally {
      setDownloading(false);
    }
  };

  // ponytail: Abhaengigkeit ist die Dokument-ID, nicht das Objekt — sonst laedt ein
  // inline erzeugtes `document={{...}}` bei jedem Render neu. `getUrl` wandert
  // absichtlich nicht in die Liste (Aufrufer geben eine Inline-Funktion mit).
  useEffect(() => {
    if (!doc || !canPreviewDocument(doc)) return;

    let cancelled = false;
    let objectUrl = null;
    setUrl(null);
    setFailed(false);

    (async () => {
      try {
        const raw = getUrl
          ? await getUrl(doc)
          : await getDocumentURL(doc.id, {
              userCountry: user?.country,
              ...options,
            });
        // Nochmal durch fetch und als typisierter Blob: S3 liefert manche Dateien als
        // octet-stream aus, und dann bietet das iframe einen Download an statt das
        // PDF zu zeigen. Klappt das nicht (CORS), reicht die URL direkt.
        let next = raw;
        try {
          const blob = await fetch(raw).then((r) => r.blob());
          objectUrl = window.URL.createObjectURL(
            new Blob([blob], { type: doc.fileType || "application/pdf" }),
          );
          next = objectUrl;
        } catch {
          /* rohe URL verwenden */
        }
        if (cancelled) return;
        setUrl(next);
      } catch (error) {
        console.error("Vorschau fehlgeschlagen", error);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.id, doc?.fileType, doc?.name, user?.country]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      show={!!doc}
      onHide={onHide}
      onExited={() => setShown(null)}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center justify-content-between flex-grow-1 me-2 overflow-hidden">
          <Modal.Title className="h5 text-truncate mb-0">
            <HiddenValue dataClass="documents">{view?.name}</HiddenValue>
          </Modal.Title>
          <Button
            variant="outline-primary"
            size="sm"
            className="ms-2 text-nowrap"
            onClick={handleDownload}
            disabled={downloading}
            title={t("Download")}
          >
            {downloading ? (
              <Spinner className="spinner-border-sm me-1" tag="span" />
            ) : (
              <i className="mdi mdi-download me-1" />
            )}
            {t("Download")}
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 400 }}>
        {!canPreviewDocument(view) ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              {t("No preview available for this file type. Please download it instead.")}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              title={t("Download")}
            >
              {downloading ? (
                <Spinner className="spinner-border-sm me-1" tag="span" />
              ) : (
                <i className="mdi mdi-download me-1" />
              )}
              {t("Download")}
            </Button>
          </div>
        ) : failed ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              {t("The document couldn't be loaded. Please refresh the page and try again.")}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              title={t("Download")}
            >
              {downloading ? (
                <Spinner className="spinner-border-sm me-1" tag="span" />
              ) : (
                <i className="mdi mdi-download me-1" />
              )}
              {t("Download")}
            </Button>
          </div>
        ) : !url ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner />
          </div>
        ) : isPdf(view) ? (
          <iframe
            src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            // Masked falls back to a generic string rather than `undefined`: an iframe with no
            // `title` and an image with no `alt` are an a11y error, and a screen reader then
            // reads out the URL instead.
            title={titleIfVisible(view.name, "documents") || t("Document preview")}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        ) : (
          <img
            src={url}
            alt={titleIfVisible(view.name, "documents") || t("Document preview")}
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              display: "block",
              margin: "0 auto",
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentPreviewModal;
